import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Card, Tabs, Spin, Empty, Modal,
  Form, Input, Select, InputNumber, Typography, Tooltip, message,
  Badge, Alert, DatePicker, Row, Col, Statistic,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, InboxOutlined,
  WarningOutlined, ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getStockApi, createStockApi, addStockApi, consumeStockApi, getStockLogsApi } from '../../api/stock';
import { getCropListFilterApi } from '../../api/crops';
import { getAccountsApi } from '../../api/accounts';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PRIMARY = '#1a6b2a';

const apiErr = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
const LOW_STOCK_THRESHOLD = 10;

const CATEGORIES = [
  { value: 'fertilizer', label: 'Fertilizer', color: 'green' },
  { value: 'seed',       label: 'Seed',       color: 'blue'  },
  { value: 'pesticide',  label: 'Pesticide',  color: 'red'   },
  { value: 'tool',       label: 'Tool',       color: 'purple'},
  { value: 'fuel',       label: 'Fuel',       color: 'orange'},
  { value: 'other',      label: 'Other',      color: 'default'},
];

const UNITS = [
  { value: 'kg',     label: 'kg' },
  { value: 'g',      label: 'g' },
  { value: 'liter',  label: 'Liter' },
  { value: 'ml',     label: 'ml' },
  { value: 'bag',    label: 'Bag' },
  { value: 'packet', label: 'Packet' },
  { value: 'piece',  label: 'Piece' },
];

function categoryColor(cat) {
  return CATEGORIES.find((c) => c.value === cat)?.color || 'default';
}
function categoryLabel(cat) {
  return CATEGORIES.find((c) => c.value === cat)?.label || cat;
}

// ─── Create Stock Item Modal ────────────────────────────────────────────────
function CreateStockModal({ open, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const qty   = Form.useWatch('quantity',      form);
  const price = Form.useWatch('price_per_unit', form);
  const amount = qty && price ? (qty * price).toFixed(2) : null;

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setLoading(true);
    try {
      const res = await createStockApi(values);
      if (res.data?.status === 'error') throw new Error(res.data.message);
      message.success('Stock item created!');
      onSuccess();
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to create stock item'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Create Stock Item"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          Create
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Item Name" name="name" rules={[{ required: true, message: 'Enter item name' }]} style={{ gridColumn: '1/-1' }}>
            <Input placeholder="e.g. Urea" />
          </Form.Item>
          <Form.Item label="Category" name="category" rules={[{ required: true }]}>
            <Select placeholder="Select category" options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))} />
          </Form.Item>
          <Form.Item label="Unit" name="unit" rules={[{ required: true }]}>
            <Select placeholder="Select unit" options={UNITS} />
          </Form.Item>
          <Form.Item label="Initial Quantity" name="quantity" rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
            <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Price per Unit (₹)" name="price_per_unit" rules={[{ required: true }, { type: 'number', min: 0, message: 'Must be ≥ 0' }]}>
            <InputNumber placeholder="0.00" style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>
        {amount && (
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 14px', marginBottom: 12 }}>
            <Text style={{ color: PRIMARY }}>Total Value: <strong>₹{amount}</strong></Text>
          </div>
        )}
        <Form.Item label="Note (optional)" name="note">
          <Input.TextArea rows={2} placeholder="e.g. Initial stock" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Add Stock Modal ────────────────────────────────────────────────────────
function AddStockModal({ open, onClose, onSuccess, stockItems, vendors }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const qty   = Form.useWatch('quantity',       form);
  const price = Form.useWatch('price_per_unit', form);
  const amount = qty && price ? (qty * price).toFixed(2) : null;

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleItemChange = (id) => {
    const item = stockItems.find((i) => i.id === id);
    if (item) form.setFieldValue('price_per_unit', Number(item.price_per_unit) || 0);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setLoading(true);
    try {
      const res = await addStockApi(values);
      if (res.data?.status === 'error') throw new Error(res.data.message);
      message.success('Stock added successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to add stock'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Space><ArrowUpOutlined style={{ color: '#52c41a' }} />Add Stock</Space>}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          Add Stock
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label="Stock Item" name="stock_item_id" rules={[{ required: true, message: 'Select a stock item' }]}>
          <Select
            placeholder="Select item"
            onChange={handleItemChange}
            options={stockItems.map((i) => ({ value: i.id, label: `${i.name} (${i.quantity} ${i.unit})` }))}
            showSearch
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item label="Vendor (optional)" name="vendor_id">
          <Select
            placeholder="Select vendor"
            allowClear
            showSearch
            options={vendors.map((v) => ({ value: v.id, label: v.name + (v.mobile ? ` — ${v.mobile}` : '') }))}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Quantity" name="quantity" rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
            <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Price per Unit (₹)" name="price_per_unit" rules={[{ required: true }, { type: 'number', min: 0 }]}>
            <InputNumber placeholder="0.00" style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>
        {amount && (
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 14px', marginBottom: 12 }}>
            <Text style={{ color: PRIMARY }}>Total Purchase Cost: <strong>₹{amount}</strong></Text>
          </div>
        )}
        <Form.Item label="Note (optional)" name="note">
          <Input placeholder="e.g. New purchase from market" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Use Stock Modal ─────────────────────────────────────────────────────────
function UseStockModal({ open, onClose, onSuccess, stockItems, crops, defaultItemId }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const selectedItemId = Form.useWatch('stock_item_id', form);
  const selectedCropId = Form.useWatch('crop_id',       form);
  const qty            = Form.useWatch('quantity',       form);
  const price          = Form.useWatch('price_per_unit', form);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (defaultItemId) {
        const item = stockItems.find((i) => i.id === defaultItemId);
        form.setFieldsValue({
          stock_item_id: defaultItemId,
          price_per_unit: Number(item?.price_per_unit) || 0,
        });
      }
    }
  }, [open, defaultItemId, stockItems, form]);

  // Auto-fill price when item changes
  const handleItemChange = (id) => {
    const item = stockItems.find((i) => i.id === id);
    if (item) {
      form.setFieldValue('price_per_unit', Number(item.price_per_unit) || 0);
      form.setFieldValue('amount', null);
    }
  };

  // Auto-calculate amount when qty or price changes
  useEffect(() => {
    const q = Number(qty);
    const p = Number(price);
    if (q > 0 && p >= 0) {
      form.setFieldValue('amount', parseFloat((q * p).toFixed(2)));
    }
  }, [qty, price, form]);

  const selectedItem = stockItems.find((i) => i.id === selectedItemId);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    if (selectedItem && values.quantity > selectedItem.quantity) {
      message.error(`Not enough stock. Available: ${selectedItem.quantity} ${selectedItem.unit}`);
      return;
    }
    setLoading(true);
    try {
      const res = await consumeStockApi(values);
      if (res.data?.status === 'error') throw new Error(res.data.message);
      message.success('Stock used successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to use stock'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<Space><ArrowDownOutlined style={{ color: '#ff4d4f' }} />Use Stock</Space>}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleSave}
          style={{ background: PRIMARY, borderColor: PRIMARY }}>
          Use Stock
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" size="large">
        <Form.Item label="Stock Item" name="stock_item_id" rules={[{ required: true, message: 'Select a stock item' }]}>
          <Select
            placeholder="Select item"
            onChange={handleItemChange}
            options={stockItems.map((i) => ({
              value: i.id,
              label: `${i.name} (${i.quantity} ${i.unit} available)`,
              disabled: i.quantity <= 0,
            }))}
            showSearch
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        {selectedItem && (
          <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 14px', marginBottom: 12, border: '1px solid #f0f0f0' }}>
            <Text type="secondary">Available: </Text>
            <Text strong style={{ color: selectedItem.quantity < LOW_STOCK_THRESHOLD ? '#ff4d4f' : PRIMARY }}>
              {selectedItem.quantity} {selectedItem.unit}
            </Text>
          </div>
        )}

        <Form.Item label="Select Crop (optional)" name="crop_id">
          <Select
            placeholder="Select crop for expense tracking"
            allowClear
            options={crops.map((c) => ({
              value: c.id,
              label: `${c.crop_name || c.crop || c.name}${c.farm_name ? ` — ${c.farm_name}` : ''}`,
            }))}
            showSearch
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        {selectedCropId && (
          <Alert
            message="This will also be recorded as an expense for the selected crop."
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item label="Quantity" name="quantity"
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}>
            <InputNumber placeholder="0" style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Price per Unit (₹)" name="price_per_unit"
            rules={[{ required: true }, { type: 'number', min: 0 }]}>
            <InputNumber placeholder="0.00" style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>

        <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true }, { type: 'number', min: 0 }]}>
          <InputNumber placeholder="Auto-calculated (editable)" style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item label="Note (optional)" name="note">
          <Input placeholder="e.g. Used for wheat field" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── Main Stock Page ─────────────────────────────────────────────────────────
const currentYear = dayjs().year();
const CROP_YEARS  = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

export default function StockPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const [stockItems,  setStockItems]  = useState([]);
  const [logs,        setLogs]        = useState([]);
  const [crops,       setCrops]       = useState([]);
  const [vendors,     setVendors]     = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [loadingLogs,  setLoadingLogs]  = useState(false);
  const [activeTab,   setActiveTab]   = useState('items');

  // Modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen,    setAddOpen]    = useState(false);
  const [useOpen,    setUseOpen]    = useState(false);
  const [defaultUseItem, setDefaultUseItem] = useState(null);

  // Log filters
  const [logTypeFilter, setLogTypeFilter]   = useState('all');
  const [logDateRange,  setLogDateRange]    = useState(null);

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const res = await getStockApi();
      setStockItems(res.data?.data || []);
    } catch (err) {
      message.error(apiErr(err, 'Failed to load stock items'));
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = {};
      if (logTypeFilter !== 'all') params.type = logTypeFilter;
      if (logDateRange?.[0]) params.from = logDateRange[0].format('YYYY-MM-DD');
      if (logDateRange?.[1]) params.to   = logDateRange[1].format('YYYY-MM-DD');
      const res = await getStockLogsApi(params);
      const raw = res.data?.data;
      setLogs(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    } catch (err) {
      message.error(apiErr(err, 'Failed to load stock logs'));
    } finally {
      setLoadingLogs(false);
    }
  }, [logTypeFilter, logDateRange]);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await getAccountsApi({ type: 'shop_keeper' });
      setVendors(res.data?.data || res.data || []);
    } catch {
      // vendors optional — don't block UI
    }
  }, []);

  const fetchCrops = useCallback(async () => {
    try {
      const res = await getCropListFilterApi({
        years: CROP_YEARS,
        lang,
        status: '1',
        per_page: 500,
        page: 1,
      });
      const raw = res.data?.data;
      setCrops(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    } catch {
      // crops optional — don't block UI
    }
  }, [lang]);

  useEffect(() => { fetchStock(); fetchCrops(); fetchLogs(); fetchVendors(); }, [fetchStock, fetchCrops, fetchLogs, fetchVendors]);
  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
  }, [activeTab, fetchLogs]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalItems    = stockItems.length;
  const totalValue    = stockItems.reduce((sum, i) => sum + (i.quantity * i.price_per_unit), 0);
  const lowStockItems = stockItems.filter((i) => i.quantity < LOW_STOCK_THRESHOLD);

  // ── Filtered logs ─────────────────────────────────────────────────────────
  const filteredLogs = logs.filter((log) => {
    if (logTypeFilter !== 'all' && log.type !== logTypeFilter) return false;
    return true;
  });

  // ── Stock Items Table Columns ─────────────────────────────────────────────
  const stockColumns = [
    {
      title: 'Item Name',
      dataIndex: 'name',
      render: (name, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <InboxOutlined style={{ color: PRIMARY, fontSize: 15 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{name}</div>
            {row.quantity < LOW_STOCK_THRESHOLD && (
              <div style={{ fontSize: 11, color: '#ff4d4f' }}>
                <WarningOutlined /> Low stock
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (v) => <Tag color={categoryColor(v)}>{categoryLabel(v)}</Tag>,
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (_, row) => (
        <span style={{ fontWeight: 700, color: row.quantity < LOW_STOCK_THRESHOLD ? '#ff4d4f' : '#222' }}>
          {row.quantity < LOW_STOCK_THRESHOLD && <WarningOutlined style={{ marginRight: 4 }} />}
          {row.quantity} {row.unit}
        </span>
      ),
    },
    {
      title: 'Price / Unit',
      dataIndex: 'price_per_unit',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Total Value',
      key: 'value',
      render: (_, row) => (
        <span style={{ color: PRIMARY, fontWeight: 600 }}>{formatCurrency(row.quantity * row.price_per_unit)}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Add Stock">
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              style={{ color: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => { setDefaultUseItem(null); setAddOpen(true); }}
            >
              Add
            </Button>
          </Tooltip>
          <Tooltip title="Use Stock">
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              danger
              onClick={() => { setDefaultUseItem(row.id); setUseOpen(true); }}
            >
              Use
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Log Table Columns ─────────────────────────────────────────────────────
  const logColumns = [
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (v) => (
        <Tag color={v === 'add' ? 'green' : 'red'} icon={v === 'add' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
          {v === 'add' ? 'Added' : 'Used'}
        </Tag>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (v, row) => (
        <span style={{ fontWeight: 600, color: row.type === 'add' ? '#52c41a' : '#ff4d4f' }}>
          {row.type === 'add' ? '+' : '-'}{v}
        </span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
  ];

  return (
    <div>
      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: PRIMARY }}>
          <InboxOutlined style={{ marginRight: 8 }} />
          {t('stock')}
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchStock} loading={loadingStock} />
          <Button
            icon={<ArrowUpOutlined />}
            style={{ color: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => setAddOpen(true)}
          >
            Add Stock
          </Button>
          <Button
            danger
            icon={<ArrowDownOutlined />}
            onClick={() => { setDefaultUseItem(null); setUseOpen(true); }}
          >
            Use Stock
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => setCreateOpen(true)}
          >
            New Item
          </Button>
        </Space>
      </div>

      {/* ── Low Stock Warning ─────────────────────────────────────────────── */}
      {lowStockItems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={
            <span>
              <strong>{lowStockItems.length} item{lowStockItems.length > 1 ? 's' : ''} running low:</strong>
              {' '}{lowStockItems.map((i) => `${i.name} (${i.quantity} ${i.unit})`).join(', ')}
            </span>
          }
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* ── Summary Cards ────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #f0f0f0' }}>
            <Statistic
              title="Total Stock Items"
              value={totalItems}
              prefix={<InboxOutlined />}
              valueStyle={{ color: PRIMARY }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #f0f0f0' }}>
            <Statistic
              title="Total Stock Value"
              value={totalValue}
              prefix="₹"
              precision={2}
              valueStyle={{ color: PRIMARY }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #f0f0f0' }}>
            <Statistic
              title="Low Stock Alerts"
              value={lowStockItems.length}
              prefix={<WarningOutlined />}
              valueStyle={{ color: lowStockItems.length > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'items', label: `Stock Items${totalItems ? ` (${totalItems})` : ''}` },
          { key: 'logs',  label: 'Stock Logs' },
        ]}
      />

      {/* ── Stock Items Tab ───────────────────────────────────────────────── */}
      {activeTab === 'items' && (
        <Card style={{ borderRadius: 10 }}>
          {loadingStock ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : stockItems.length === 0 ? (
            <Empty
              description="No stock items yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" icon={<PlusOutlined />}
                style={{ background: PRIMARY, borderColor: PRIMARY }}
                onClick={() => setCreateOpen(true)}>
                Create First Item
              </Button>
            </Empty>
          ) : (
            <Table
              dataSource={stockItems}
              columns={stockColumns}
              rowKey="id"
              scroll={{ x: 700 }}
              rowClassName={(row) => row.quantity < LOW_STOCK_THRESHOLD ? 'low-stock-row' : ''}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          )}
        </Card>
      )}

      {/* ── Stock Logs Tab ────────────────────────────────────────────────── */}
      {activeTab === 'logs' && (
        <Card style={{ borderRadius: 10 }}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Select
              value={logTypeFilter}
              onChange={setLogTypeFilter}
              style={{ width: 140 }}
              options={[
                { value: 'all',  label: 'All Types' },
                { value: 'add',  label: 'Added'     },
                { value: 'use',  label: 'Used'      },
              ]}
            />
            <RangePicker
              value={logDateRange}
              onChange={setLogDateRange}
              format="DD MMM YYYY"
              style={{ flex: 1, minWidth: 220 }}
            />
            <Button onClick={fetchLogs} loading={loadingLogs} icon={<ReloadOutlined />}>
              Apply
            </Button>
          </div>

          {loadingLogs ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : filteredLogs.length === 0 ? (
            <Empty description="No logs found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              dataSource={filteredLogs}
              columns={logColumns}
              rowKey="id"
              scroll={{ x: 600 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          )}
        </Card>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <CreateStockModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => { fetchStock(); fetchLogs(); }}
      />
      <AddStockModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={() => { fetchStock(); fetchLogs(); }}
        stockItems={stockItems}
        vendors={vendors}
      />
      <UseStockModal
        open={useOpen}
        onClose={() => { setUseOpen(false); setDefaultUseItem(null); }}
        onSuccess={() => { fetchStock(); fetchLogs(); }}
        stockItems={stockItems}
        crops={crops}
        defaultItemId={defaultUseItem}
      />
    </div>
  );
}
